import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Button, Grid, ReactPortal } from 'gu-ui-kit'
import styles from 'components/SidePanel/SidePanel.module.scss'
import PageHeader from 'components/PageHeader'
import { Icon, Col } from 'gu-ui-kit'
const PANEL_TRANSITION_MS = 250

const SidePanel = ({
  open = false,
  onClose,
  children = null,
  title = null,
  closeButton = true,
  scrollable = true,
}) => {
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const containerRef = useRef(null)
  const closeTimerRef = useRef(null)

  useEffect(() => {
    if (open) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }

      setMounted(true)

      const rafId = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true))
      })

      return () => cancelAnimationFrame(rafId)
    }

    setVisible(false)
  }, [open])

  const unmountPanel = useCallback(() => {
    setMounted(false)
  }, [])

  useEffect(() => {
    if (!mounted || visible) {
      return
    }

    closeTimerRef.current = setTimeout(unmountPanel, PANEL_TRANSITION_MS)

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
    }
  }, [mounted, visible, unmountPanel])

  const handleTransitionEnd = useCallback(
    (event) => {
      if (event.target !== containerRef.current || visible) {
        return
      }

      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }

      unmountPanel()
    },
    [visible, unmountPanel],
  )

  useEffect(() => {
    if (!mounted || !visible) {
      return
    }

    document.body.classList.add('body_overflow_hidden')

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.classList.remove('body_overflow_hidden')
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [mounted, visible, onClose])

  if (!mounted) {
    return null
  }

  return (
    <ReactPortal wrapperId={'side-panel-portal'}>
      <div
        className={styles['side-panel']}
        style={{ '--side-panel-transition-duration': PANEL_TRANSITION_MS + 'ms' }}
      >
        <div
          className={clsx(styles['side-panel__overlay'], {
            [styles['side-panel__overlay_open']]: visible,
          })}
          onClick={onClose}
          role={'presentation'}
        />
        <aside
          ref={containerRef}
          className={clsx(styles['side-panel__container'], {
            [styles['side-panel__container_open']]: visible,
          })}
          onTransitionEnd={handleTransitionEnd}
        >
          <Grid
            direction={'column'}
            gap={40}
            wrap={false}
            className={clsx(styles['side-panel__body'], 'layout-tile')}
          >
            {(title || closeButton) && (
              <Grid
                justifyContent={!title ? 'end' : 'between'}
                alignItems={'start'}
                wrap={false}
                direction={'row'}
                gap={16}
                className={styles['side-panel__header']}
              >
                {title && <PageHeader>{title}</PageHeader>}
                {closeButton && (
                  <Col>
                    <Button
                      onClick={onClose}
                      variant={'ghost'}
                      grey
                      startIcon={
                        <Icon
                          name={'close'}
                          color={'Interface-Seventh'}
                          width={20}
                        />
                      }
                      noLabel
                    />
                  </Col>
                )}
              </Grid>
            )}
            <div
              className={clsx(styles['side-panel__content'], {
                [styles['side-panel__content_scrollable']]: scrollable,
                [styles['side-panel__content_fixed']]: !scrollable,
              })}
            >
              {children}
            </div>
          </Grid>
        </aside>
      </div>
    </ReactPortal>
  )
}

SidePanel.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.node,
  title: PropTypes.string,
  closeButton: PropTypes.bool,
  scrollable: PropTypes.bool,
}

export default SidePanel
